import type { InvoiceItem } from "./types";

export function roundCents(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

export type LineInput = Pick<
  InvoiceItem,
  "quantity" | "unit_price_net" | "vat_rate"
>;

export interface LineTotals {
  net: number;
  vat: number;
  gross: number;
}

export function lineTotals(item: LineInput): LineTotals {
  const net = roundCents(item.quantity * item.unit_price_net);
  const vat = roundCents((net * item.vat_rate) / 100);
  const gross = roundCents(net + vat);
  return { net, vat, gross };
}

export interface VatBreakdownRow {
  rate: number;
  base: number;
  vat: number;
}

export interface InvoiceTotals {
  net: number;
  vat: number;
  gross: number;
  vatBreakdown: VatBreakdownRow[];
}

// EN 16931 (BR-CO-17): VAT is computed once per rate on the summed line nets,
// not per line, so the PDF and the e-invoice XML always agree.
export function invoiceTotals(
  items: ReadonlyArray<LineInput>
): InvoiceTotals {
  const baseByRate = new Map<number, number>();
  for (const item of items) {
    const { net } = lineTotals(item);
    baseByRate.set(
      item.vat_rate,
      roundCents((baseByRate.get(item.vat_rate) ?? 0) + net)
    );
  }
  const vatBreakdown = [...baseByRate.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([rate, base]) => ({
      rate,
      base,
      vat: roundCents((base * rate) / 100),
    }));
  let net = 0;
  let vat = 0;
  for (const row of vatBreakdown) {
    net = roundCents(net + row.base);
    vat = roundCents(vat + row.vat);
  }
  return { net, vat, gross: roundCents(net + vat), vatBreakdown };
}
